import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", email: "", subject: "", message: "", company: "" };

export const ContactForm = ({ open, onOpenChange }) => {
  const [data, setData] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) =>
    setData((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = data.name.trim();
    const email = data.email.trim();
    const message = data.message.trim();
    if (!name || !email || !message) {
      toast.error("Name, email, and message are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/submissions", {
        type: "contact",
        name,
        email,
        subject: data.subject.trim() || null,
        message,
        company: data.company || "",
      });
      toast.success("Message sent — we’ll reply by email.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (busy) return; onOpenChange(v); }}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="pf-display">Get in touch</DialogTitle>
          <DialogDescription>
            Send us a note and we’ll get back to you by email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cf-name">Name</Label>
              <Input id="cf-name" data-testid="contact-form-name" value={data.name} onChange={onChange("name")} autoComplete="name" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-email">Email</Label>
              <Input id="cf-email" type="email" data-testid="contact-form-email" value={data.email} onChange={onChange("email")} autoComplete="email" required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-subject">Subject (optional)</Label>
            <Input id="cf-subject" data-testid="contact-form-subject" value={data.subject} onChange={onChange("subject")} placeholder="e.g. Press inquiry, support, partnership" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-message">Message</Label>
            <Textarea id="cf-message" data-testid="contact-form-message" rows={5} value={data.message} onChange={onChange("message")} required />
          </div>

          {/* Honeypot — hidden from humans, bots tend to fill it */}
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", top: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          >
            <Label htmlFor="cf-company">Company</Label>
            <Input id="cf-company" tabIndex={-1} autoComplete="off" value={data.company} onChange={onChange("company")} />
          </div>

          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="contact-form-submit" className="rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send message
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactForm;
